<template>
  <v-row class="app-ag-grid" no-gutters>
    <v-col cols="12">
      <v-card class="app-grid-card" flat>
        <!-- TOOLBAR — plain flexbox, not Vuetify's v-col breakpoints.
             v-col md=/cols= switch on *window* width, but this component
             often renders next to a persistent sidebar, so the available
             space can be much narrower than the window — that mismatch
             caused the title/search to stack even on normal desktop
             windows. Flex-wrap responds to the actual rendered width. -->
        <div class="app-grid-toolbar">
          <div class="app-grid-toolbar-head">
            <div class="app-grid-title">
              <h2>{{ title }}</h2>
            </div>

            <div class="app-grid-toolbar-actions">
              <!-- ADD -->
              <v-tooltip v-if="showNewBtn" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click="onAdd">
                    <v-icon color="#ffffff" size="20">mdi-plus</v-icon>
                  </v-btn>
                </template>

                <span>ADD</span>
              </v-tooltip>

              <!-- REFRESH -->
              <v-tooltip v-if="refreshBtn" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click="onRefresh">
                    <v-icon color="#ffffff" size="20">mdi-refresh</v-icon>
                  </v-btn>
                </template>

                <span>Refresh</span>
              </v-tooltip>
            </div>
          </div>

          <!-- SEARCH -->
          <v-text-field
            v-model="quickFilter"
            dense
            outlined
            clearable
            hide-details
            placeholder="Search..."
            prepend-inner-icon="mdi-magnify"
            class="app-grid-search"
          />
        </div>

        <!-- GRID (desktop / tablet) -->
        <v-row v-if="!useMobileCards" no-gutters>
          <v-col cols="12">
            <ag-grid-vue
              class="ag-theme-alpine app-grid-modern"
              :style="height"
              :columnDefs="columnDefs"
              :rowData="rowData"
              :defaultColDef="defaultColDef"
              :rowSelection="rowSelection"
              :pagination="pagination"
              :paginationPageSize="pageSize"
              :animateRows="true"
              :overlayLoadingTemplate="loadingTemplate"
              :overlayNoRowsTemplate="noRowsTemplate"
              :getRowStyle="getRowStyle"
              :pinnedBottomRowData="pinnedBottomRowData"
              :getRowClass="getRowClass"
              :headerHeight="36"
              :floatingFiltersHeight="26"
              @grid-ready="onGridReady"
              @selection-changed="onSelectionChanged"
              @cell-clicked="onCellClicked"
              @row-double-clicked="onRowDoubleClicked"
            />
          </v-col>
        </v-row>

        <!-- CARD LIST (mobile) -->
        <div v-else class="app-grid-cards">
          <div v-if="loading" class="app-grid-overlay">
            <span class="loader"></span>
            <span>Loading...</span>
          </div>

          <div v-else-if="!filteredRows.length" class="app-grid-overlay muted">
            No records found
          </div>

          <div
            v-else
            v-for="(row, i) in filteredRows"
            :key="row._id || i"
            class="app-grid-card-item"
          >
            <div class="app-grid-card-item-top">
              <div class="app-grid-card-item-title">
                {{ mobileCard.title(row) }}
              </div>
              <div
                v-if="mobileCard.badge"
                class="app-grid-card-item-badge"
                :style="mobileCard.badge(row).style"
              >
                {{ mobileCard.badge(row).text }}
              </div>
            </div>

            <div v-if="mobileCard.subtitle" class="app-grid-card-item-subtitle">
              {{ mobileCard.subtitle(row) }}
            </div>

            <div class="app-grid-card-item-fields">
              <div
                v-for="f in mobileCard.fields(row)"
                :key="f.label"
                class="app-grid-card-item-field"
              >
                <span class="field-label">{{ f.label }}</span>
                <span class="field-value">{{ f.value }}</span>
              </div>
            </div>

            <div v-if="mobileCard.actions" class="app-grid-card-item-actions">
              <v-btn
                v-for="action in mobileCard.actions(row)"
                :key="action.label"
                text
                small
                :color="action.color || 'primary'"
                class="app-grid-card-item-action-btn"
                @click.stop="action.onClick(row)"
              >
                <v-icon left size="16">{{ action.icon }}</v-icon>
                {{ action.label }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";

export default {
  name: "AppAgGrid",
  components: { AgGridVue },

  props: {
    title: {
      type: String,
      default: "Data Grid",
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    rowData: {
      type: Array,
      default: () => [],
    },
    rowSelection: {
      type: String,
      default: "single",
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    pageSize: {
      type: Number,
      default: 50,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    newBtn: {
      type: Boolean,
      default: true,
    },
    refreshBtn: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: "height: calc(100vh - 160px)",
    },
    getRowStyle: {
      type: Function,
      default: null,
    },
    // Describes how to render each row as a card on mobile. When omitted,
    // the grid falls back to ag-Grid's own horizontal scroll on mobile too.
    // Shape: { title(row), subtitle(row)?, badge(row)?, fields(row), actions(row)? }
    //   - title/subtitle: strings
    //   - badge: { text, style } for a small colored chip (e.g. status)
    //   - fields: [{ label, value }] shown as a compact key/value list
    //   - actions: [{ label, icon, color?, onClick(row) }] — explicit
    //     tap targets only. The card body itself is not tappable on
    //     mobile (accidental taps are too easy/costly on touch), so any
    //     navigation must go through one of these buttons.
    mobileCard: {
      type: Object,
      default: null,
    },
    // Optional bold, pinned totals row — pass a single-row array in the
    // same shape as rowData, e.g. [{ code: "", name: "Total", debit: 4500, credit: 4500 }].
    // Standard on any real ledger/statement grid (Trial Balance, GST
    // Reports, P&L) so the grand total is always visible without
    // scrolling to the last row.
    pinnedBottomRowData: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      gridApi: null,
      columnApi: null,
      quickFilter: "",

      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true,
        suppressMenu: true,
        // A column is treated as numeric/currency purely from what it
        // actually renders, not from guessing off the field/header name
        // (too fragile — "Credit Note #" isn't a number). Deliberately
        // requires a currency sign, decimal point, or percent sign — a
        // bare digit string alone (phone number, pincode, a plain count
        // column) is NOT enough to qualify, or every Phone/PAN/ID column
        // in the app would wrongly right-align as if it were money.
        // Detected per-cell so a mixed column (e.g. "-" for N/A rows)
        // still aligns correctly row by row. Right-aligned + tabular
        // numerals is the one visual habit every real ledger shares.
        cellClass: (params) => {
          // An un-formatted raw number (no valueFormatter on the column)
          // is unambiguous — every identifier-like field in this app
          // (phone, GSTIN, pincode) is stored as a String, never a
          // number, so a bare JS number here is always a real quantity.
          if (params.valueFormatted == null && typeof params.value === "number") {
            return "app-cell app-cell--num";
          }
          const text = params.valueFormatted ?? params.value;
          const str = typeof text === "string" ? text.trim() : "";
          const isCurrencyOrPercent = /^-?₹[\d,]+(\.\d+)?$|^-?[\d,]+\.\d+$|^-?[\d,]+(\.\d+)?%$/.test(str);
          return isCurrencyOrPercent ? "app-cell app-cell--num" : "app-cell";
        },
      },

      loadingTemplate: `
        <span class="app-grid-overlay">
          <span class="loader"></span>
          <span>Loading...</span>
        </span>
      `,

      noRowsTemplate: `
        <span class="app-grid-overlay muted">
          No records found
        </span>
      `,
    };
  },

  watch: {
    loading(val) {
      if (!this.gridApi) return;
      val ? this.gridApi.showLoadingOverlay() : this.gridApi.hideOverlay();
    },

    quickFilter(val) {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(val);
      }
    },
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    useMobileCards() {
      return this.isMobile && !!this.mobileCard;
    },

    filteredRows() {
      const q = (this.quickFilter || "").trim().toLowerCase();
      if (!q) return this.rowData;

      return this.rowData.filter((row) => {
        const haystack = JSON.stringify(row).toLowerCase();
        return haystack.includes(q);
      });
    },

    // Single choke point for hiding the ADD button for a VIEW_ONLY team
    // member, instead of threading a prop through every one of this
    // component's ~15 callers. Belt-and-suspenders UI polish only — the
    // backend's requireWriteAccess middleware is the real enforcement.
    showNewBtn() {
      return this.newBtn && !this.isViewOnly;
    },
  },

  methods: {
    // Zebra striping — alternating row tint is standard on every real
    // ledger/spreadsheet so the eye can track a row across many columns
    // without a ruler. Skips pinned rows (the totals row gets its own
    // bolder styling instead, see .ag-row-pinned in the stylesheet).
    getRowClass(params) {
      if (params.node.rowPinned) return "";
      return params.node.rowIndex % 2 === 1 ? "app-row-alt" : "";
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      if (this.loading) {
        this.gridApi.showLoadingOverlay();
      }

      this.sizeColumnsToFit();
      window.addEventListener("resize", this.sizeColumnsToFit);

      this.$emit("grid-ready", params);
    },

    sizeColumnsToFit() {
      if (!this.gridApi) return;

      this.gridApi.sizeColumnsToFit();
    },

    onSelectionChanged() {
      this.$emit("selection-change", this.gridApi.getSelectedRows());
    },

    onCellClicked(params) {
      this.$emit("cell-click", params);
    },

    onRefresh() {
      this.$emit("refresh");
    },

    onAdd() {
      this.$emit("add");
    },

    onRowDoubleClicked(params) {
      this.$emit("row-dbl-click", params.data);
    },
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.sizeColumnsToFit);
  },
};
</script>

<style>
/* Card */
.app-grid-card {
  border-radius: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* Toolbar — flexbox that wraps based on the space actually available to
   this component, not the browser window width. Vuetify's v-col
   md=/cols= breakpoints look at window width only, which broke when this
   grid rendered next to the persistent sidebar: a normal ~1200-1400px
   browser window has well under 960px of *content* width once the
   sidebar is subtracted, so md= columns silently fell back to full-width
   stacking even though there was visibly enough room. */
.app-grid-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 20px;
  padding: 10px 18px;
  background: var(--pl-base-bg-gradient);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.app-grid-toolbar-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 240px;
  min-width: 0;
}

.app-grid-title {
  min-width: 0;
  flex: 1 1 auto;
}

.app-grid-title h2 {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff !important;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-grid-toolbar-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.app-grid-search {
  flex: 1 1 260px;
  max-width: 320px;
}

@media (max-width: 600px) {
  .app-grid-toolbar {
    padding: 10px 14px;
  }

  .app-grid-title h2 {
    font-size: 16px;
  }

  .app-grid-search {
    flex-basis: 100%;
    max-width: none;
  }
}

/* Search */
.app-grid-search .v-input__control {
  background: #ffffff;
  border-radius: 0;
  min-height: 34px !important;
}

.app-grid-search .v-input__slot {
  min-height: 34px !important;
  padding: 0 10px !important;
}

.app-grid-search input {
  font-size: 13px;
}

/* Grid */
.app-grid-modern {
  width: 100%;
  /* height: calc(100vh - 160px); */
  background: #ffffff;
}

/* Header — a firm double rule under the label row (a ledger-paper cue:
   the line that separates column headings from figures) instead of just
   a soft shadow. */
.app-grid-modern .ag-header {
  background: var(--pl-base-bg-gradient);
  border-bottom: 2px solid rgba(255, 255, 255, 0.35);
}

.app-grid-modern .ag-header-row-column {
  height: 36px !important;
}

.app-grid-modern .ag-header-cell {
  border-right: 1px solid rgba(255, 255, 255, 0.15);
}

.app-grid-modern .ag-header-cell-label {
  color: #ffffff;
  font-weight: 700;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.app-grid-modern .ag-header-cell-text {
  overflow: visible;
}

.app-grid-modern .ag-header-icon {
  color: rgba(255, 255, 255, 0.85) !important;
  fill: rgba(255, 255, 255, 0.85) !important;
}

.app-grid-modern .ag-header-cell:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Floating filter row — a small input under each header, always visible
   (no menu to open) so "column filter" reads as a feature, not a hidden
   context-menu option. */
.app-grid-modern .ag-header-row-floating-filter {
  height: 26px !important;
}

.app-grid-modern .ag-floating-filter {
  background: #ffffff;
  padding: 0 !important;
  height: 100%;
}

.app-grid-modern .ag-floating-filter-body {
  height: 100%;
  margin: 0 !important;
}

.app-grid-modern .ag-floating-filter-input {
  height: 100%;
}

.app-grid-modern .ag-floating-filter-input input {
  font-size: 12px;
  border-radius: 0;
  border: none;
  border-right: 1px solid #dfe4ea;
  padding: 2px 7px;
  height: 100%;
  width: 100%;
}

.app-grid-modern .ag-floating-filter-input input:focus {
  outline: none;
  background: #eef6fb;
  box-shadow: inset 0 -2px 0 #0a5a90;
}

.app-grid-modern .ag-floating-filter-button {
  display: none;
}

/* Rows */
.app-grid-modern .ag-row {
  border-bottom: 1px solid #eef1f5;
  transition: background 0.15s ease;
}

/* Zebra striping — the single biggest thing that makes a dense grid of
   figures readable at a glance instead of a wall of numbers; every real
   ledger/spreadsheet does this. Kept faint so it never competes with the
   hover/selected states below. */
.app-grid-modern .ag-row.app-row-alt {
  background: #f8fafc;
}

.app-grid-modern .ag-row:hover {
  background: linear-gradient(
    90deg,
    rgba(10, 90, 144, 0.06),
    rgba(21, 163, 85, 0.06)
  ) !important;
}

/* Selected */
.app-grid-modern .ag-row-selected {
  background: rgba(21, 163, 85, 0.15) !important;
}

/* Column separators — a faint vertical rule between every cell, the
   other habit that reads unmistakably as "ledger" rather than a generic
   borderless web list. */
.app-grid-modern .ag-cell {
  border-right: 1px solid #f1f4f8;
}

/* Cells */
.app-cell {
  font-size: 13.5px;
  font-weight: 500;
  color: #1f2937;
  display: flex;
  align-items: center;
}

/* Numeric/currency cells — right-aligned with tabular numerals so every
   amount in a column lines up on its ones place and decimal point, the
   way figures line up in Tally, Excel, or a printed statement. Detected
   automatically per-cell in cellClass above; no per-page changes needed
   for existing Amount/Debit/Credit/Balance/GST columns anywhere. */
.app-cell.app-cell--num {
  justify-content: flex-end;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  font-family: ui-monospace, "SF Mono", "Cascadia Mono", "Segoe UI Mono", Consolas, monospace;
  font-size: 13px;
  color: #16233d;
}

/* Pinned totals row — bold, separated by a heavier top rule, and shaded
   so a grand total (Trial Balance, GST Reports, P&L) is unmistakably the
   sum row and not just another data row. Opt-in via the pinnedBottomRowData prop. */
.app-grid-modern .ag-floating-bottom {
  border-top: 2px solid #16233d;
}
.app-grid-modern .ag-row-pinned {
  background: #eef2f7 !important;
  font-weight: 700;
}
.app-grid-modern .ag-row-pinned .app-cell {
  color: #16233d;
  font-weight: 700;
}
.app-grid-modern .ag-row-pinned .app-cell--num {
  font-weight: 700;
}

/* Pagination */
.app-grid-modern .ag-paging-panel {
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  padding: 10px 16px;
  font-size: 13px;
}

/* Overlay */
.app-grid-overlay {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* Loader */
.loader {
  width: 28px;
  height: 28px;
  border: 3px solid #e5e7eb;
  border-top-color: #0a5a90;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ag-paging-panel {
  flex-wrap: nowrap !important;
  white-space: nowrap;
  overflow-x: auto;
}

.ag-paging-row-summary-panel,
.ag-paging-page-summary-panel {
  white-space: nowrap;
}

/* ================= MOBILE CARD LIST ================= */
.app-grid-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.app-grid-card-item {
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 16px;
  background: #fbfbfd;
}

.app-grid-card-item-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.app-grid-card-item-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}

.app-grid-card-item-badge {
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 20px;
  background: #eef3fb;
  color: #0a5a90;
}

.app-grid-card-item-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

.app-grid-card-item-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 10px;
}

.app-grid-card-item-field {
  display: flex;
  flex-direction: column;
  min-width: 90px;
}

.app-grid-card-item-field .field-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.app-grid-card-item-field .field-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-top: 1px;
}

.app-grid-card-item-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef1f5;
}

.app-grid-card-item-action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;
}
</style>
